import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  Paper,
  Stack,
  Theme,
  Typography,
} from '@mui/material'
import UndoIcon from '@mui/icons-material/Undo'
import { makeStyles } from '@mui/styles'
import ReactFamilyTree from 'react-family-tree'
import PinchZoomPan from '../../../../screens/App/FamilyMember/FamilyTree/PinchZoomPan'
import {
  ExtNode,
  FamilyNode,
  FamilyType,
  RelType,
  SubFamilyType,
} from '../../../../screens/App/FamilyMember/FamilyTree/types'
import SingleFamilyNode from '../../../../screens/App/FamilyMember/FamilyTree/SingleFamilyNode'
import { nodeWidth } from './const'
import { nodeHeight } from '../../../../screens/App/FamilyMember/FamilyTree/const'
import {
  Generation,
  MemberResponse,
  PermissionType,
  RelationResponse,
  User,
  UserPermissions,
} from '../../../../shared/types'
import CheckIcon from '@mui/icons-material/Check'
import ButtonComponent from '../../../../shared/components/ButtonComponent'
import { useSelector } from 'react-redux'
import { getMembers, getRelations } from '../../../../store/data/data.selectors'
import DateAndPlace, { DateAndPlaceType } from '../../../../screens/App/FamilyMember/FamilyTree/Templates/DateAndPlace'
import SettingsIcon from '@mui/icons-material/Settings'
import { getUser, getUserPermissions } from '../../../../store/settings/settings.selectors'
import DatePickerComponent from '../../../../shared/components/DatePickerComponent'
import getDateFromCustomDate from '../../../../util/getDateFromCustomDate'
import TextInputComponent from '../../../../shared/components/TextInputComponent'
import AddIcon from '@mui/icons-material/Add'
import DropDownComponent, { DropDownElement } from '../../../../shared/components/DropDownComponent'
import CheckBoxComponent from '../../../../shared/components/CheckBoxComponent'

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    width: 'calc(100% - 50px)',
    margin: '25px',
    display: 'flex',
    flexDirection: 'column',
    height: '75vh',
    minHeight: '500px',
  },
  tree: {
    borderRadius: '4px',
  },
  contentBox: {
    margin: '10px 0',
  },
  memberName: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    padding: '20px',
    margin: '40px 0 0 0',
    fontSize: '16px',
  },
  settingsIcon: {
    position: 'absolute',
    top: theme.spacing(),
    right: theme.spacing(),
    color: theme.palette.primary.main,
    cursor: 'pointer',
  },
  icons: {
    transform: 'scale(1.2)',
  },
}))
const FamilyTree: React.FC = () => {
  const classes = useStyles()
  const initialRootId = 'OttoMeissner'

  const members: MemberResponse | undefined = useSelector(getMembers)
  const user: User | undefined = useSelector(getUser)
  const relations: RelationResponse | undefined = useSelector(getRelations)
  const userPermissions: UserPermissions = useSelector(getUserPermissions)

  const [layer, setLayer] = useState<number>(0)
  const [family, setFamily] = useState<FamilyType | undefined | null>()

  const [nodes, setNodes] = useState<FamilyNode[]>(relations?.results || [])
  const [filteredNodes, setFilteredNodes] = useState<FamilyNode[]>(relations?.results || [])
  const [temporaryNodes, setTemporaryNodes] = useState<FamilyNode[] | undefined>()

  const [rootId, setRootId] = useState(initialRootId)

  const [selectId, setSelectId] = useState<string | undefined>()
  const [showDialog, setShowDialog] = useState<boolean>(false)

  const [selectedNode, setSelectedNode] = useState<FamilyNode>()

  useEffect(() => {
    filterNodes(FamilyType.Root)
  }, [nodes])

  useEffect(() => {
    setSelectedNode(nodes.find(item => item.id === selectId))
  }, [nodes, selectId])

  const selectedMember = useMemo(() => (
    members?.results.find(item => item.id === selectId)
  ), [nodes, selectId])

  const [selectedMemberDead, setSelectedMemberDead] = useState<boolean>(false)

  useEffect(() => {
    setSelectedMemberDead(selectedMember?.dead || false)
  }, [selectedMember])

  const getMember = (id?: string) => {
    if (id) {
      return members?.results.find(item => item.id === id)
    }
  }

  const getRelationOptions = (type: 'parent' | 'child' | 'sibling' | 'spouse') => {
    switch (type) {
      case 'parent':
        return [
          {
            key: RelType.blood,
            value: 'leibliches Elternteil',
          },
          {
            key: RelType.adopted,
            value: 'Pflegeelternteil',
          },
          {
            key: RelType.adopted,
            value: 'Adoptivelternteil',
          }]
      case 'child':
        return [
          {
            key: RelType.blood,
            value: 'leibliches Kind',
          },
          {
            key: RelType.adopted,
            value: 'Pflegekind',
          },
          {
            key: RelType.adopted,
            value: 'Adoptivkind',
          }]
      case 'sibling':
        return [
          {
            key: RelType.blood,
            value: 'Geschwisterkind',
          },
          {
            key: RelType.half,
            value: 'Halbgeschwisterkind',
          },
        ]
      case 'spouse':
        return [
          {
            key: RelType.married,
            value: 'Ehepartner',
          },
          {
            key: RelType.partnership,
            value: 'Partner',
          },
        ]
    }
  }
  const getParentOptions = (memberId: string) => {
    const options: DropDownElement[] = []
    members?.results.map(member => {
      if (member.id !== memberId) {
        if (!selectedNode?.parents.find(elem => elem.id === member.id) &&
          !selectedNode?.children.find(elem => elem.id === member.id) &&
          !selectedNode?.spouses.find(elem => elem.id === member.id) &&
          !selectedNode?.siblings.find(elem => elem.id === member.id)
        ) {
          member.id && options.push({
            key: member.id,
            value: `${member.firstname} ${member.lastname}`,
          })
        }
      }
    })
    return options
  }

  const [adminMode, setAdminMode] = useState<boolean>(false)
  const [newRelation, setNewRelation] = useState<RelType | undefined>()

  const [addParent, setAddParent] = useState<boolean>(false)
  const [newParent, setNewParent] = useState<string | undefined>()

  const filterNodes = (family: FamilyType, subFamily?: SubFamilyType) => {
    if (family !== FamilyType.Root) {
      let filteredNodes = nodes.filter(node => node.family === family)
      if (subFamily) {
        filteredNodes = filteredNodes.filter(node => node.subFamily === subFamily)
      }
      const cleanedFilteredNodes = filteredNodes.map(node => {
        if (node.family === FamilyType.IlseKaiser) {
          return ({
            ...node,
            parents: node.generation === Generation.orange ? [] : node.parents,
          })
        } else {
          if (subFamily) {
            return ({
              ...node,
              parents: node.generation === Generation.blue ? [] : node.parents,
            })
          } else {
            if (node.family === FamilyType.ThomasKuther || node.family === FamilyType.HeinzMichael) {
              return ({
                ...node,
                parents: node.generation === Generation.green ? [] : node.parents,
                children: node.generation === Generation.blue ? [] : node.children,
                spouses: node.generation === Generation.blue ? [] : node.spouses,
              })
            } else {
              return ({
                ...node,
                parents: node.generation === Generation.green ? [] : node.parents,
              })
            }
          }
        }
      })
      setFilteredNodes(cleanedFilteredNodes)
    } else {
      const rootNodes = nodes.filter(node => node.family === FamilyType.Root)
      const ilse = nodes.find(node => node.id === 'IlseKaiser')
      ilse && rootNodes.push(ilse)
      const thomas = nodes.find(node => node.id === 'ThomasKuther')
      thomas && rootNodes.push(thomas)
      const andras = nodes.find(node => node.id === 'AndreasKuther')
      andras && rootNodes.push(andras)
      const heike = nodes.find(node => node.id === 'HeikeKuther')
      heike && rootNodes.push(heike)
      const juergen = nodes.find(node => node.id === 'JuergenKuther')
      juergen && rootNodes.push(juergen)
      const heinz = nodes.find(node => node.id === 'HeinzMichael')
      heinz && rootNodes.push(heinz)
      const cleanedRootNodes = rootNodes.map(node => {
        if (node.family === FamilyType.IlseKaiser) {
          return ({
            ...node,
            children: node.generation === Generation.orange ? [] : node.children,
            spouses: node.generation === Generation.orange ? [] : node.spouses,
          })
        } else {
          return ({
            ...node,
            children: node.generation === Generation.green ? [] : node.children,
            spouses: node.generation === Generation.green ? [] : node.spouses,
          })
        }
      })
      setFilteredNodes(cleanedRootNodes)
    }
  }

  const familyTypeArray = ['JuergenKuther', 'AndreasKuther', 'HeikeKuther', 'ThomasKuther', 'HeinzMichael', 'Root', 'IlseKaiser']
  const familySubTypeArray = [
    'SilkeSchuetz', 'GerlindDerdar', 'MargitBronkalla', 'RobertFriessleben', 'AnkeMueller', 'ClaudiaPrange',
    'TassiloLudwig', 'RameshKuther', 'CosimaKuther',
    'MaikaVerbeek', 'DirkMichael', 'RonaldMichael', 'RoyMichael',
  ]

  return (
    <>
      <Paper className={classes.paper}>
        <Typography variant='h1' sx={{ marginLeft: '10px' }}>Stammbaum</Typography>
        {layer > 0 &&
          <Box
            sx={{ margin: '10px', cursor: 'pointer' }}
            onClick={() => {
              if (layer === 1) {
                setLayer(0)
                filterNodes(FamilyType.Root)
                setRootId(initialRootId)
                setFamily(undefined)
              }
              if (layer === 2 && family) {
                setLayer(1)
                filterNodes(family)
                setRootId(family as string)
              }
            }}
          ><UndoIcon />
          </Box>}
        <PinchZoomPan min={0.5} max={0.8} captureWheel>
          <ReactFamilyTree
            nodes={filteredNodes}
            rootId={rootId}
            width={nodeWidth}
            height={nodeHeight}
            className={classes.tree}
            renderNode={(node: Readonly<ExtNode>) => {
              return (
                <SingleFamilyNode
                  node={node}
                  onClick={(id) => {
                    if (familyTypeArray.find(elem => elem === id) && layer === 0) {
                      // zoom into tree
                      filterNodes(id as FamilyType)
                      setRootId(id)
                      setLayer(layer + 1)
                    } else {
                      if (familySubTypeArray.find(elem => elem === id) && layer === 1) {
                        // zoom into tree
                        const familyType = nodes.filter(elem => elem.subFamily === id)[0].family
                        familyType && filterNodes(familyType, id as SubFamilyType)
                        setRootId(id)
                        setLayer(layer + 1)
                        setFamily(familyType)
                      } else {
                        setShowDialog(true)
                        setSelectId(id)
                      }
                    }
                  }}
                />
              )
            }}
          />
        </PinchZoomPan>
      </Paper>
      {/* DetailDialog */}
      <Dialog
        open={showDialog}
        onClose={() => {
          setShowDialog(false)
        }}
        sx={{ '& .MuiDialog-paper': { borderRadius: 0, minWidth: '600px', maxWidth: '600px' } }}
      >
        <Box className={classes.memberName}>
          {`${selectedMember?.firstname} ${selectedMember?.middlenames ? selectedMember?.middlenames : ''} ${selectedMember?.lastname} ${selectedMember?.birthname ? `(geb. ${selectedMember?.birthname})` : ''}`}
        </Box>
        <Stack sx={{ p: 2 }}>
          <DialogContent sx={{ p: 0, overflow: 'visible' }}>
            {/* TODO false löschen und bearbeiten fertig stellen */}
            {(userPermissions.relations.find(elem => elem === PermissionType.update) || selectedNode?.id === user?.memberId) && !adminMode && false &&
              <SettingsIcon
                className={`${classes.settingsIcon} ${classes.icons}`}
                onClick={() => {
                  setAdminMode(!adminMode)
                }}
              />}

            {adminMode ? (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box className={classes.contentBox}>
                      <DatePickerComponent
                        label='Geboren am'
                        fullWidth
                        value={selectedMember?.birthday ? getDateFromCustomDate(selectedMember?.birthday) : undefined}
                      />
                      <TextInputComponent
                        label='Geboren in'
                        clearable={false}
                        fullWidth
                        value={selectedMember?.placeOfBirth}
                      />
                      <CheckBoxComponent
                        value={selectedMemberDead}
                        label='Verstorben'
                        onChange={(value) => {
                          setSelectedMemberDead(value)
                        }}
                      />
                      {selectedMemberDead &&
                        <>
                          <DatePickerComponent
                            label='Gestorben am'
                            fullWidth
                            value={selectedMember?.deathday ? getDateFromCustomDate(selectedMember?.deathday) : undefined}
                          />
                          <TextInputComponent
                            label='Gestorben in'
                            fullWidth
                            clearable={false}
                            value={selectedMember?.placeOfDeath}
                          />
                        </>}
                    </Box>

                    {/* Eltern */}
                    <Box className={classes.contentBox}>
                      <Box
                        sx={{ display: 'flex', justifyContent: 'flex-start', aligItems: 'center', flexWrap: 'nowrap' }}
                      >
                        <Typography><b>Eltern</b></Typography>
                        {selectedNode?.parents.length && selectedNode?.parents.length < 2 && false &&
                          <IconButton
                            onClick={() => setAddParent(true)}
                          ><AddIcon />
                          </IconButton>}
                      </Box>
                      <Box
                        sx={{ display: 'flex', justifyContent: 'flex-start', aligItems: 'center', flexWrap: 'nowrap' }}
                      >
                        <Typography>
                          {selectedNode?.parents[0] && `${getMember(selectedNode?.parents[0].id)?.firstname} ${getMember(selectedNode?.parents[0].id)?.lastname}`}
                        </Typography>
                      </Box>
                      <Typography>
                        {selectedNode?.parents[1] && `${getMember(selectedNode?.parents[1].id)?.firstname} ${getMember(selectedNode?.parents[1].id)?.lastname}`}
                      </Typography>
                      {
                        addParent && selectedNode?.id &&
                          <Box
                            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
                          >
                            <Box sx={{ marginRight: '10px' }}>
                              <DropDownComponent
                                options={getParentOptions(selectedNode?.id)}
                                label='Neues Elternteil'
                                onChange={(value) => setNewParent(value.key)}
                              />
                            </Box>
                            <Box sx={{ marginRight: '10px' }}>
                              <DropDownComponent
                                options={getRelationOptions('parent')}
                                label='Beziehung'
                                onChange={(value) => setNewRelation(value.key as RelType)}
                              />
                            </Box>
                            <Box sx={{ marginRight: '10px' }}>
                              <IconButton
                                disabled={!newParent || !newRelation}
                                onClick={() => {
                                  setTemporaryNodes(nodes)
                                  const newNodes = [...nodes]
                                  // newParent && newNodes.find(node => node.id === selectedNode.id)?.parents.push({
                                  //   id: newParent,
                                  //   type: newRelation as RelType,
                                  // })
                                  // newParent && newNodes.find(node => node.id === newParent)?.children.push({
                                  //   id: selectedNode.id,
                                  //   type: newRelation as RelType,
                                  // })
                                  setNodes(newNodes)
                                  setAddParent(false)
                                  setNewParent(undefined)
                                  setNewRelation(undefined)
                                }}
                              >
                                <CheckIcon />
                              </IconButton>
                            </Box>
                          </Box>
                      }
                    </Box>

                    {/* Geschwister */}
                    <Box className={classes.contentBox}>
                      <Typography><b>Geschwister</b></Typography>
                      {selectedNode?.siblings.map((sibling, index) =>
                        <Typography key={index}>
                          {`${getMember(sibling.id)?.firstname} ${getMember(sibling.id)?.lastname}`}
                        </Typography>
                        )}
                    </Box>

                    {/* Partner */}
                    <Box className={classes.contentBox}>
                      <Typography><b>Partner</b></Typography>
                      {selectedNode?.spouses.map((spouse, index) =>
                        <Typography key={index}>
                          {`${getMember(spouse.id)?.firstname} ${getMember(spouse.id)?.lastname}`}
                        </Typography>
                        )}
                    </Box>

                    {/* Kinder */}
                    <Box className={classes.contentBox}>
                      <Typography><b>Kinder</b></Typography>
                      {selectedNode?.children.map((child, index) =>
                        <Typography key={index}>
                          {`${getMember(child.id)?.firstname} ${getMember(child.id)?.lastname}`}
                        </Typography>
                        )}
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <img
                      style={{ width: '100%', height: 'auto', margin: '10px 0' }}
                      alt=''
                      src={selectedMember?.profileImage ? selectedMember?.profileImage : `/profileImages/${selectedMember?.id}.jpg`}
                    />
                  </Grid>
                </Grid>
              </>)
              : (
                <>
                  <Grid container>
                    <Grid item xs={6}>
                      {selectedMember?.showDetails &&
                        <Box className={classes.contentBox}>
                          <Typography>
                            <DateAndPlace
                              date={selectedMember?.birthday}
                              place={selectedMember?.placeOfBirth}
                              type={DateAndPlaceType.birth}
                            />
                          </Typography>
                          <Typography>
                            <DateAndPlace
                              date={selectedMember?.deathday}
                              place={selectedMember?.placeOfDeath}
                              type={DateAndPlaceType.death}
                            />
                          </Typography>
                        </Box>}
                      {selectedNode?.parents && selectedNode?.parents.length > 0 &&
                        <Box className={classes.contentBox}>
                          <Typography><b>Eltern</b></Typography>
                          <Typography>
                            {selectedNode?.parents[0] && `${getMember(selectedNode?.parents[0].id)?.firstname} ${getMember(selectedNode?.parents[0].id)?.lastname}`}
                          </Typography>
                          <Typography>
                            {selectedNode?.parents[1] && `${getMember(selectedNode?.parents[1].id)?.firstname} ${getMember(selectedNode?.parents[1].id)?.lastname}`}
                          </Typography>
                        </Box>}
                      {selectedNode?.siblings && selectedNode?.siblings.length > 0 &&
                        <Box className={classes.contentBox}>
                          <Typography><b>Geschwister</b></Typography>
                          {selectedNode?.siblings.map((sibling, index) =>
                            <Typography key={index}>
                              {`${getMember(sibling.id)?.firstname} ${getMember(sibling.id)?.lastname}`}
                            </Typography>
                          )}
                        </Box>}
                      {selectedNode?.spouses && selectedNode?.spouses.length > 0 &&
                        <Box className={classes.contentBox}>
                          <Typography><b>Partner</b></Typography>
                          {selectedNode?.spouses.map((spouse, index) =>
                            <Typography key={index}>
                              {`${getMember(spouse.id)?.firstname} ${getMember(spouse.id)?.lastname}`}
                            </Typography>
                          )}
                        </Box>}
                      {selectedNode?.children && selectedNode?.children.length > 0 &&
                        <Box className={classes.contentBox}>
                          <Typography><b>Kinder</b></Typography>
                          {selectedNode?.children.map((child, index) =>
                            <Typography key={index}>
                              {`${getMember(child.id)?.firstname} ${getMember(child.id)?.lastname}`}
                            </Typography>
                          )}
                        </Box>}
                    </Grid>
                    <Grid item xs={6}>
                      <img
                        style={{ width: '100%', height: 'auto', margin: '10px 0' }}
                        alt=''
                        src={selectedMember?.profileImage ? selectedMember?.profileImage : `/profileImages/${selectedMember?.id}.jpg`}
                      />
                    </Grid>
                  </Grid>
                </>)}
          </DialogContent>
        </Stack>
        <DialogActions sx={{ p: 3 }}>
          {adminMode ? (
            <>
              <ButtonComponent
                color='secondary'
                variant='contained'
                text='Abbrechen'
                onClick={() => {
                  temporaryNodes && setNodes(temporaryNodes)
                  setTemporaryNodes(undefined)
                  setAdminMode(false)
                }}
              />
              <ButtonComponent
                color='primary'
                variant='contained'
                text='Speichern'
                onClick={() => {
                  setAdminMode(false)
                  setTemporaryNodes(undefined)
                  // TODO save new settings
                }}
              />
            </>
          )
            : (
              <ButtonComponent
                color='primary'
                variant='contained'
                text='Ok'
                onClick={() => {
                  setShowDialog(false)
                }}
              />
            )}
        </DialogActions>
      </Dialog>
    </>
  )
}

export default FamilyTree
